import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AnimatedDemo.css';

// Animated "build a portfolio, then analyze it" walkthrough. Shows how easy the
// builder is: for each holding you enter a Ticker, an Avg Price, and Shares,
// then hit Add — the first holding types out slowly to teach the flow, the rest
// fill in quickly. Then "Analyze Portfolio" grades every position.
const HOLDINGS = [
  { sym: 'NVDA', price: '142.50', shares: '50' },
  { sym: 'TSM', price: '168.00', shares: '40' },
  { sym: 'GOOGL', price: '175.20', shares: '30' },
  { sym: 'COIN', price: '210.00', shares: '15' },
];

// Results after analysis. `price` is the current price; `pnl` is derived from the
// avg price entered above so the numbers stay internally consistent.
const ROWS = [
  { sym: 'NVDA', price: '$177.39', pnl: '+24.5%', q: 82, s: 76, conv: 4.2, action: 'Favorable', actionClass: 'scale',
    tests: [
      { name: 'ROIC vs WACC', score: 95 },
      { name: 'Moat Strength', score: 91 },
      { name: 'Relative Strength', score: 82 },
      { name: 'DCF Upside', score: 74 },
    ],
  },
  { sym: 'TSM', price: '$213.50', pnl: '+27.1%', q: 91, s: 72, conv: 4.5, action: 'Favorable', actionClass: 'scale' },
  { sym: 'GOOGL', price: '$205.10', pnl: '+17.1%', q: 88, s: 54, conv: 3.5, action: 'Neutral', actionClass: 'hold' },
  { sym: 'COIN', price: '$184.30', pnl: '-12.2%', q: 45, s: 28, conv: 2.4, action: 'Cautious', actionClass: 'trim' },
];

export default function PortfolioDemo({ autoPlay = true, loop = true }) {
  const [phase, setPhase] = useState('build'); // 'build' | 'progress' | 'results'
  const [typedTicker, setTypedTicker] = useState('');
  const [typedPrice, setTypedPrice] = useState('');
  const [typedShares, setTypedShares] = useState('');
  const [activeField, setActiveField] = useState(null);
  const [addedHoldings, setAddedHoldings] = useState([]);
  const [analyzeClicked, setAnalyzeClicked] = useState(false);
  const [progress, setProgress] = useState(0);
  const [visibleRows, setVisibleRows] = useState(0);
  const [expandedSym, setExpandedSym] = useState(null);
  const [testBarWidths, setTestBarWidths] = useState({});
  const timers = useRef([]);

  const clearTimers = () => {
    timers.current.forEach((t) => { clearTimeout(t); clearInterval(t); });
    timers.current = [];
  };

  const runDemo = useCallback(() => {
    clearTimers();
    setPhase('build'); setTypedTicker(''); setTypedPrice(''); setTypedShares('');
    setActiveField(null); setAddedHoldings([]); setAnalyzeClicked(false);
    setProgress(0); setVisibleRows(0); setExpandedSym(null); setTestBarWidths({});

    const push = (delay, fn) => timers.current.push(setTimeout(fn, delay));
    // Schedule char-by-char typing into `setter`; returns the end time.
    const typeInto = (start, text, setter, rate) => {
      for (let i = 1; i <= text.length; i++) {
        timers.current.push(setTimeout(() => setter(text.slice(0, i)), start + i * rate));
      }
      return start + text.length * rate + 100;
    };

    let t = 500;
    HOLDINGS.forEach((h, idx) => {
      const rate = idx === 0 ? 60 : 32; // first holding slow to teach; rest quick
      push(t, () => setActiveField('ticker'));
      t = typeInto(t, h.sym, setTypedTicker, rate) + 120;
      push(t, () => setActiveField('price'));
      t = typeInto(t, h.price, setTypedPrice, rate) + 120;
      push(t, () => setActiveField('shares'));
      t = typeInto(t, h.shares, setTypedShares, rate) + 120;
      // "+ Add" → the holding drops into the list and the fields clear.
      push(t, () => {
        setAddedHoldings((prev) => [...prev, h]);
        setTypedTicker(''); setTypedPrice(''); setTypedShares(''); setActiveField(null);
      });
      t += 550;
    });

    // "Analyze Portfolio"
    push(t, () => setAnalyzeClicked(true));
    t += 400;
    push(t, () => {
      setPhase('progress');
      let prog = 0;
      const progInt = setInterval(() => {
        prog += Math.random() * 25 + 12;
        if (prog >= 100) { prog = 100; clearInterval(progInt); }
        setProgress(Math.min(prog, 100));
      }, 350);
      timers.current.push(progInt);
    });
    t += 1600;

    // Results appear row by row.
    push(t, () => {
      setPhase('results');
      let row = 0;
      const rowInt = setInterval(() => {
        row++;
        setVisibleRows(row);
        if (row >= ROWS.length) clearInterval(rowInt);
      }, 350);
      timers.current.push(rowInt);
    });
    t += ROWS.length * 350 + 400;

    // Auto-expand NVDA's individual test scores.
    push(t, () => setExpandedSym('NVDA'));
    push(t + 200, () => setTestBarWidths({ filled: true }));
  }, []);

  useEffect(() => {
    if (!autoPlay) return;
    runDemo();
    if (!loop) return () => clearTimers();
    const loopId = setInterval(runDemo, 18000);
    return () => { clearInterval(loopId); clearTimers(); };
  }, [autoPlay, loop, runDemo]);

  const scoreClass = (v, max) => { const pct = (v / max) * 100; return pct >= 66 ? 'high' : pct >= 33 ? 'mid' : 'low'; };

  return (
    <div className="demo-container">
      <div className="demo-titlebar">
        <span className="demo-dot red" /><span className="demo-dot yellow" /><span className="demo-dot green" />
        <span className="demo-titlebar-text">alphagrade.io/portfolio</span>
      </div>
      <div className="demo-body">
        {phase === 'build' && (
          <>
            <div className="pf-add-form">
              <div className={`pf-field ${activeField === 'ticker' ? 'active' : ''}`}>
                <span className="pf-field-label">Ticker</span>
                <span className="pf-field-val">{typedTicker}{activeField === 'ticker' && <span className="demo-cursor" />}</span>
              </div>
              <div className={`pf-field ${activeField === 'price' ? 'active' : ''}`}>
                <span className="pf-field-label">Avg Price</span>
                <span className="pf-field-val">{typedPrice ? `$${typedPrice}` : ''}{activeField === 'price' && <span className="demo-cursor" />}</span>
              </div>
              <div className={`pf-field ${activeField === 'shares' ? 'active' : ''}`}>
                <span className="pf-field-label">Shares</span>
                <span className="pf-field-val">{typedShares}{activeField === 'shares' && <span className="demo-cursor" />}</span>
              </div>
              <span className="pf-add-btn">+ Add</span>
            </div>

            <div className="pf-holdings">
              {addedHoldings.map((h) => (
                <div key={h.sym} className="pf-holding-row">
                  <span className="pf-h-sym">{h.sym}</span>
                  <span className="pf-h-meta">{h.shares} sh @ ${h.price}</span>
                </div>
              ))}
            </div>

            {addedHoldings.length > 0 && (
              <div className="pf-analyze-row">
                <span className={`demo-btn ${analyzeClicked ? 'clicked' : ''}`}>
                  {analyzeClicked ? 'Analyzing…' : 'Analyze Portfolio'}
                </span>
              </div>
            )}
          </>
        )}

        {phase === 'progress' && (
          <div className="pf-analyzing">
            <div className="pf-analyzing-label">Analyzing {ROWS.length} positions…</div>
            <div className="demo-progress">
              <div className="demo-progress-fill" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {phase === 'results' && (
          <table className="demo-table">
            <thead>
              <tr>
                <th>Symbol</th><th>Price</th><th>P&L</th><th>Quality</th><th>Setup</th><th>Conviction</th><th>Signal</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.slice(0, visibleRows).map((r, i) => (
                <React.Fragment key={r.sym}>
                  <tr style={{ animation: `slideInUp 0.3s ease ${i * 0.08}s both` }}>
                    <td className="sym">
                      {r.tests && <span style={{ fontSize: '0.6rem', marginRight: 3, color: 'var(--text-muted)' }}>{expandedSym === r.sym ? '▼' : '▶'}</span>}
                      {r.sym}
                    </td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}>{r.price}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: r.pnl.startsWith('+') ? '#10B981' : '#EF4444' }}>{r.pnl}</td>
                    <td><span className={`score ${scoreClass(r.q, 100)}`}>{r.q}</span></td>
                    <td><span className={`score ${scoreClass(r.s, 100)}`}>{r.s}</span></td>
                    <td><span className={`score ${scoreClass(r.conv, 5)}`}>{r.conv}</span></td>
                    <td><span className={`action ${r.actionClass}`}>{r.action}</span></td>
                  </tr>
                  {expandedSym === r.sym && r.tests && (
                    <tr style={{ animation: 'slideInUp 0.2s ease both' }}>
                      <td colSpan="7" style={{ padding: '0.5rem 0.6rem', background: 'var(--bg-elevated, #1A1F2E)', borderTop: '1px solid var(--border-subtle, #1E293B)' }}>
                        <div style={{ fontSize: '0.65rem', color: 'var(--neon-green, #00D4AA)', fontWeight: 700, marginBottom: '0.35rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                          Individual Test Scores
                        </div>
                        {r.tests.map((tst) => (
                          <div key={tst.name} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.2rem' }}>
                            <span style={{ fontSize: '0.65rem', color: 'var(--text-secondary)', minWidth: 85, fontWeight: 500 }}>{tst.name}</span>
                            <div style={{ flex: 1, height: 5, background: 'var(--bg-primary, #0C1220)', borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ height: '100%', borderRadius: 3, width: testBarWidths.filled ? `${tst.score}%` : '0%', background: tst.score >= 66 ? '#6495ED' : tst.score >= 33 ? '#FBBF24' : '#EF4444', transition: 'width 0.5s ease' }} />
                            </div>
                            <span style={{ fontSize: '0.65rem', fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--text-primary)', minWidth: 28, textAlign: 'right' }}>{tst.score}%</span>
                          </div>
                        ))}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
